import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../../lib/api";
import { useToast } from "../../lib/toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCamera, faTrash } from "@fortawesome/free-solid-svg-icons";

interface Product {
  id: string; sku: string; name: string; brand: string | null;
  image_url: string | null; price: number; stock: number; category_name?: string;
}

const fmt = (n: number) => n.toLocaleString("ko-KR");

export default function AdminProducts() {
  const [products, setProducts] = useState<Product[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [q, setQ] = useState("");
  const { push } = useToast();

  const load = useCallback(() => {
    setLoading(true);
    const qs = q ? `&q=${encodeURIComponent(q)}` : "";
    api.get<{ items: Product[]; total: number }>(`/products?limit=60${qs}`)
      .then((r) => { setProducts(r.items); setTotal(r.total); })
      .finally(() => setLoading(false));
  }, [q]);

  useEffect(() => { load(); }, [load]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const qs = q ? `&q=${encodeURIComponent(q)}` : "";
      const r = await api.get<{ items: Product[]; total: number }>(`/products?limit=60&offset=${products.length}${qs}`);
      setProducts((prev) => [...prev, ...r.items]);
      setTotal(r.total);
    } finally { setLoadingMore(false); }
  };

  const uploadImage = async (product: Product, file: File) => {
    if (!file.type.startsWith("image/")) { push("이미지 파일만 업로드 가능합니다", "error"); return; }
    if (file.size > 5 * 1024 * 1024) { push("5MB 이하 파일만 가능합니다", "error"); return; }
    try {
      const res = await fetch(`/api/admin/products/${product.id}/image`, {
        method: "POST",
        headers: { "Content-Type": file.type },
        body: await file.arrayBuffer(),
        credentials: "include",
      });
      const data = await res.json() as any;
      if (!res.ok) throw new Error(data.error ?? "업로드 실패");
      setProducts((prev) => prev.map((p) => p.id === product.id ? { ...p, image_url: data.image_url } : p));
      push(`${product.sku} 이미지 업로드 완료`, "success");
    } catch (e: any) {
      push(e.message, "error");
    }
  };

  const deleteImage = async (product: Product) => {
    if (!window.confirm(`${product.sku} 이미지를 삭제할까요?`)) return;
    try {
      await fetch(`/api/admin/products/${product.id}/image`, { method: "DELETE", credentials: "include" });
      setProducts((prev) => prev.map((p) => p.id === product.id ? { ...p, image_url: null } : p));
      push(`${product.sku} 이미지 삭제됨`, "info");
    } catch (e: any) {
      push(e.message, "error");
    }
  };

  const noImage = products.filter((p) => !p.image_url).length;

  return (
    <>
      <div className="row" style={{ justifyContent: "space-between", marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
        <h2 className="h-section" style={{ margin: 0 }}>
          상품 이미지 관리 <span className="muted" style={{ fontSize: 14 }}>({products.length}/{total})</span>
          {noImage > 0 && <span className="badge badge-warning" style={{ marginLeft: 8 }}>이미지 없음 {noImage}</span>}
        </h2>
        <input placeholder="SKU / 상품명 검색" value={q} onChange={(e) => setQ(e.target.value)}
          style={{ width: 240 }} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))", gap: 12 }}>
        {loading ? (
          Array.from({ length: 12 }).map((_, i) => (
            <div key={i} className="card" style={{ padding: 12 }}>
              <div className="skel" style={{ aspectRatio: "1/1", marginBottom: 8 }} />
              <div className="skel" style={{ height: 12, width: "60%" }} />
            </div>
          ))
        ) : products.map((p) => (
          <ProductCard key={p.id} product={p} onUpload={uploadImage} onDelete={deleteImage} />
        ))}
      </div>

      {!loading && products.length < total && (
        <div style={{ textAlign: "center", marginTop: 16 }}>
          <button className="btn btn-outline btn-sm" onClick={loadMore} disabled={loadingMore}>
            {loadingMore ? "불러오는 중..." : `더 보기 (${products.length}/${total})`}
          </button>
        </div>
      )}
    </>
  );
}

function ProductCard({ product, onUpload, onDelete }: {
  product: Product;
  onUpload: (p: Product, f: File) => void;
  onDelete: (p: Product) => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);

  return (
    <div className="card" style={{ padding: 12, display: "flex", flexDirection: "column", gap: 8 }}>
      <div
        style={{
          aspectRatio: "1/1", background: "var(--bg-muted)", borderRadius: "var(--r-sm)",
          display: "grid", placeItems: "center", overflow: "hidden", position: "relative",
          cursor: "pointer", border: product.image_url ? "none" : "2px dashed var(--border-strong)",
        }}
        onClick={() => fileRef.current?.click()}
        title="클릭하여 이미지 업로드"
      >
        {product.image_url ? (
          <img src={product.image_url} alt={product.name} loading="lazy"
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }}
            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }} />
        ) : (
          <div style={{ textAlign: "center", color: "var(--text-muted)" }}>
            <FontAwesomeIcon icon={faCamera} style={{ width: 28, height: 28, marginBottom: 4 }} />
            <div style={{ fontSize: 11 }}>클릭하여 업로드</div>
          </div>
        )}
        <input ref={fileRef} type="file" accept="image/*" hidden
          onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload(product, f); e.target.value = ""; }} />
      </div>
      <div>
        <div className="mono muted" style={{ fontSize: 11 }}>{product.sku}</div>
        <div style={{ fontWeight: 600, fontSize: 13, lineHeight: 1.3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {product.name}
        </div>
        <div className="muted" style={{ fontSize: 12 }}>{fmt(product.price)}원 · 재고 {product.stock}</div>
      </div>
      {product.image_url && (
        <button className="btn btn-ghost btn-sm" onClick={() => onDelete(product)}
          style={{ color: "var(--danger)", fontSize: 12 }}>
          <FontAwesomeIcon icon={faTrash} style={{ width: 10, height: 10 }} /> 이미지 삭제
        </button>
      )}
    </div>
  );
}
